import {
  CopilotChatCountBar,
  CopilotChats,
  CopilotName,
  CopilotSupportButton,
  useCopilotHistory,
  useNavigateToAgentChat,
} from '@/features/copilot';
import { close, open, useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Box, Button, Heading, Icon, PlainButton, Text, TextLabel, Tooltip } from '@customerio/pluma-components/react';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { generatePath } from '@/services/router/generate-path';
import { useQueryState } from 'nuqs';
import { isInternalLink } from '@/utils/url';
import * as styles from './layout.css';

import { useMountEffect } from '@/hooks/use-mount-effect';

interface AgentLayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
}

export function AgentLayout({ children, title }: AgentLayoutProps) {
  const isCopilotSidebarActive = useSidebarActive('copilot');
  const navigateToChat = useNavigateToAgentChat();
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();
  const [aiFrom] = useQueryState('ai-from');

  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const homePath = generatePath('/workspaces/:workspaceId/home', { workspaceId });
  const internalFrom = aiFrom != null ? isInternalLink(aiFrom) : null;
  const exitTarget = internalFrom?.relativePath ?? homePath;
  const history = useCopilotHistory();
  const totalChats = history.data?.pages?.[0]?.meta?.pagination?.total ?? 0;

  useMountEffect(() => {
    if (isCopilotSidebarActive) {
      close();
    }
  });

  return (
    <div className={styles.agentLayout}>
      {/* History sidebar */}
      <div className={styles.historySidebar}>
        <Box
          display="flex"
          alignItems="center"
          px="250"
          style={{
            borderBottom: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
            height: '45px',
            minHeight: '45px',
          }}
        >
          <TextLabel as="div" color="base">
            Recent chats
          </TextLabel>
        </Box>
        {totalChats > 0 ? <CopilotChatCountBar total={totalChats} /> : null}
        <div className={styles.historySidebarContent}>
          <CopilotChats location="fullscreen" />
        </div>
        <div className={styles.historySidebarFooter}>
          <Box display="flex" flexDirection="column" px="150" py="150" gap="0">
            <PlainButton
              href={generatePath('/workspaces/:workspaceId/settings/ai-business-profile', { workspaceId })}
              className={styles.sidebarFooterLink}
            >
              <Icon name="sparkle" size="sm" color="subtle" />
              <Text size="s" weight="medium">
                Context
              </Text>
            </PlainButton>
            <PlainButton href="/settings/privacy" className={styles.sidebarFooterLink}>
              <Icon name="manage" size="sm" color="subtle" />
              <Text size="s" weight="medium">
                AI settings
              </Text>
            </PlainButton>
          </Box>
        </div>
      </div>

      {/* Main panel */}
      <div className={styles.mainPanel}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px="200"
          py="100"
          backgroundColor="base"
          className={styles.headerBar}
        >
          <Box display="flex" alignItems="center" gap="100">
            <Heading level="4">{title ?? <CopilotName />}</Heading>
          </Box>
          <Box display="flex" alignItems="center" gap="100">
            <CopilotSupportButton />
            <Tooltip content="Exit full screen" isCustom={true}>
              {(context) => (
                <Button
                  variant="subtle"
                  size="sm"
                  icon="collapse"
                  isIconOnly={true}
                  onClick={() => {
                    track('aiAssistant', 'collapsedFromFullScreen', {});
                    open('copilot');
                    safeNavigate(exitTarget);
                  }}
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                />
              )}
            </Tooltip>
            <Button
              variant="subtle"
              size="sm"
              href={generatePath('/workspaces/:workspaceId/agent/routine', { workspaceId })}
            >
              Routine
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon="add"
              onClick={() => {
                track('aiAssistant', 'newChatClicked', {});
                navigateToChat();
              }}
            >
              New chat
            </Button>
          </Box>
        </Box>

        {/* Content */}
        {children}
      </div>
    </div>
  );
}
